import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('docsite_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function CredentialCard({ kind, title, preview, current, pending, onUpload, onApprove, onRevoke, busy }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [bgDark, setBgDark] = useState(false);

  const onFile = (f) => {
    if (!f) return;
    if (f.type !== 'image/png') {
      toast.error('Only transparent-background PNG files are accepted.');
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      toast.error('File is larger than 3 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setLocalPreview(e.target.result);
    reader.readAsDataURL(f);
    onUpload(f);
  };

  return (
    <Card className="p-6 flex flex-col gap-4" data-testid={`credential-card-${kind}`}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#123B91]">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {current
              ? <>Version <b>v{current.versionId}</b> · <span className="font-mono text-[10px]">{String(current.assetHash).slice(0, 12)}…</span></>
              : <>System auto-default is currently used on final Rx PDFs.</>}
          </p>
        </div>
        {current && (
          <span
            className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
            data-testid={`credential-status-${kind}`}
          >Approved</span>
        )}
      </div>

      {/* Preview area — dual background so alpha channel is verifiable */}
      <div
        className={`relative rounded-lg overflow-hidden border-2 border-dashed transition-colors
          ${dragOver ? 'border-[#0E9F8E] bg-teal-50' : 'border-slate-300'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files[0]); }}
        style={{ background: bgDark ? '#0B2F7C' : '#F5F9FD', minHeight: kind === 'stamp' ? 200 : 120 }}
        data-testid={`credential-preview-${kind}`}
      >
        {(localPreview || preview) ? (
          <img
            src={localPreview || preview}
            alt={`${kind} preview`}
            className="mx-auto my-4"
            style={{
              maxWidth: kind === 'stamp' ? 180 : 320,
              maxHeight: kind === 'stamp' ? 180 : 110,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-slate-400 text-sm">
            <span>Drop transparent PNG here</span>
            <span className="text-xs">or click below to browse</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={bgDark} onChange={(e) => setBgDark(e.target.checked)} />
          <span>Preview on dark background</span>
        </label>
        <span>PNG · alpha channel required · ≤ 3 MB</span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        className="hidden"
        onChange={(e) => onFile(e.target.files[0])}
        data-testid={`credential-input-${kind}`}
      />

      <div className="flex gap-3">
        <Button
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="flex-1 bg-[#123B91] hover:bg-[#0B2F7C] text-white"
          data-testid={`credential-upload-btn-${kind}`}
        >
          {current ? 'Replace' : 'Upload'} {kind}
        </Button>
        {pending && (
          <Button
            disabled={busy}
            onClick={onApprove}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`credential-approve-btn-${kind}`}
          >
            I approve
          </Button>
        )}
        {current && (
          <Button
            variant="outline"
            disabled={busy}
            onClick={onRevoke}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid={`credential-revoke-btn-${kind}`}
          >
            Revoke
          </Button>
        )}
      </div>
      {pending && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2" data-testid={`credential-pending-${kind}`}>
          Uploaded — awaiting your one-tap approval. Click <b>I approve</b> to make it active for all future prescriptions.
        </div>
      )}
    </Card>
  );
}

export default function DoctorCredentialsPage() {
  const [sig, setSig] = useState(null);
  const [stp, setStp] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [s, t] = await Promise.all([
        fetch(`${API}/api/dashboard/credentials/signature/current`, { headers: authHeaders() }).then((r) => r.json()),
        fetch(`${API}/api/dashboard/credentials/stamp/current`, { headers: authHeaders() }).then((r) => r.json()),
      ]);
      setSig({ current: s.current, pending: s.pending });
      setStp({ current: t.current, pending: t.pending });
    } catch (e) {
      toast.error('Could not load credential status');
    }
  };

  useEffect(() => { refresh(); }, []);

  const uploadOne = async (kind, file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/dashboard/credentials/${kind}/upload`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail?.error || `Upload failed (${r.status})`);
      toast.success(`${kind[0].toUpperCase() + kind.slice(1)} uploaded. Type "I approve" to activate.`);
      await refresh();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const approveOne = async (kind, versionId) => {
    const typed = window.prompt(`Type "I approve" to make this your active ${kind}. This is a one-time consent; every prescription from now on will use this ${kind}.`);
    if (!typed) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/dashboard/credentials/${kind}/${versionId}/approve`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ typed }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail?.expected || body?.detail?.error || 'Approval failed');
      toast.success(`${kind} activated. All future prescriptions will use it.`);
      await refresh();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const revokeOne = async (kind) => {
    if (!window.confirm(`Revoke current ${kind}? Prescriptions will fall back to the system default until you upload a replacement.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/dashboard/credentials/${kind}/revoke?reason=manual`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail?.error || 'Revoke failed');
      toast.success(`${kind} revoked. System default is now used.`);
      await refresh();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="credentials-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#123B91] tracking-tight">Prescription Credentials</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Upload your transparent signature PNG and stamp PNG. Until you do, every finalised prescription will carry a
          system-generated default (your name in a signature-style script, and a circular stamp with your name and
          registration number). Uploaded assets replace the defaults instantly.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <CredentialCard
          kind="signature"
          title="Digital Signature"
          preview={null}
          current={sig?.current}
          pending={sig?.pending}
          busy={busy}
          onUpload={(f) => uploadOne('signature', f)}
          onApprove={() => approveOne('signature', sig?.pending?.versionId)}
          onRevoke={() => revokeOne('signature')}
        />
        <CredentialCard
          kind="stamp"
          title="Doctor Stamp"
          preview={null}
          current={stp?.current}
          pending={stp?.pending}
          busy={busy}
          onUpload={(f) => uploadOne('stamp', f)}
          onApprove={() => approveOne('stamp', stp?.pending?.versionId)}
          onRevoke={() => revokeOne('stamp')}
        />
      </div>

      <div className="mt-8 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700">
        <b className="text-[#123B91]">What happens next:</b> The very next Rx PDF you finalise will use your uploaded
        artwork. Previously issued prescriptions are immutable — they keep the signature/stamp version they were
        issued with. Every issued Rx PDF records the SHA-256 of the assets used at the moment of issuance.
      </div>
    </div>
  );
}
